/**
 * QR Code Service
 * 
 * Handles CRUD operations for QR codes and action execution.
 * Supports action types: play_phoneme, navigate
 * 
 * NOTE: Uses type assertions for qr_codes table since it may not be in
 * generated types yet. Run migration then regenerate types to fix.
 */

import { supabase } from '@/lib/supabase/client';
import { playPhoneme, ensureAudioReady } from '@/lib/voice-agent/audio-engine';
import type {
  QRActionType,
  QRActionPayload,
  QRPlayPhonemePayload,
  QRNavigatePayload,
  QRActionResult,
} from '@/lib/voice-agent/types';

// Re-export types for convenience
export type { QRActionType, QRActionPayload, QRPlayPhonemePayload, QRNavigatePayload, QRActionResult };

// =============================================================================
// TYPES
// =============================================================================

/** Image type for multi-image consonant cards */
export type CardImageType = 'plain' | 'illustrated';

/** A single card image entry in the card_images JSONB array */
export interface CardImage {
  type: CardImageType;
  path: string;
}

export interface QRCode {
  id: string;
  name: string;
  code: string;
  action_type: QRActionType;
  action_payload: QRActionPayload | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  card_image_path: string | null;
  /** Multi-image support: array of {type, path} objects */
  card_images: CardImage[];
}

// Extended type for QR codes with card images (for consonant practice)
export interface QRCodeWithCard extends QRCode {
  card_image_path: string | null;
  card_images: CardImage[];
}

/**
 * Available phonemes for play_phoneme action
 * Grouped by vowels (colors) and consonants
 */
export const AVAILABLE_PHONEMES = {
  vowels: [
    { id: 'black', label: 'Black - /æ/ (cat)', color: '#000000' },
    { id: 'red', label: 'Red - /ɛ/ (bed)', color: '#EF4444' },
    { id: 'green', label: 'Green - /iː/ (see)', color: '#22C55E' },
    { id: 'blue', label: 'Blue - /uː/ (moon)', color: '#3B82F6' },
    { id: 'pink', label: 'Pink - /ɪ/ (sit)', color: '#FFB7CE' },
    { id: 'yellow', label: 'Yellow - /oʊ/ (go)', color: '#EAB308' },
    { id: 'orange', label: 'Orange - /ɔːr/ (for)', color: '#F97316' },
    { id: 'brown', label: 'Brown - /aʊ/ (cow)', color: '#92400E' },
    { id: 'plum', label: 'Plum - /ʌ/ (cup)', color: '#7C3AED' },
    { id: 'aqua', label: 'Aqua - /ɔː/ (saw)', color: '#06B6D4' },
    { id: 'lime', label: 'Lime - /aɪ/ (my)', color: '#84CC16' },
    { id: 'wood', label: 'Wood - /ʊ/ (book)', color: '#78350F' },
  ],
  consonants: [
    { id: 'b', label: 'B - /b/' },
    { id: 'c', label: 'C - /k/' },
    { id: 'd', label: 'D - /d/' },
    { id: 'f', label: 'F - /f/' },
    { id: 'g', label: 'G - /g/' },
    { id: 'h', label: 'H - /h/' },
    { id: 'j', label: 'J - /dʒ/' },
    { id: 'k', label: 'K - /k/' },
    { id: 'l', label: 'L - /l/' },
    { id: 'm', label: 'M - /m/' },
    { id: 'n', label: 'N - /n/' },
    { id: 'p', label: 'P - /p/' },
    { id: 'r', label: 'R - /r/' },
    { id: 's', label: 'S - /s/' },
    { id: 't', label: 'T - /t/' },
    { id: 'v', label: 'V - /v/' },
    { id: 'w', label: 'W - /w/' },
    { id: 'x', label: 'X - /ks/' },
    { id: 'y', label: 'Y - /j/' },
    { id: 'z', label: 'Z - /z/' },
    { id: 'ck', label: 'CK - /k/' },
  ],
} as const;

// Type assertion helper for tables not yet in generated types
 
const qrCodesTable = () => supabase.from('qr_codes' as any);

export interface CreateQRCodeInput {
  name: string;
  code?: string; // If not provided, will generate one
  action_type?: QRActionType;
  action_payload?: QRActionPayload;
}

export interface UpdateQRCodeInput {
  name?: string;
  action_type?: QRActionType;
  action_payload?: QRActionPayload;
  is_active?: boolean;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Generate a unique, URL-safe code for QR encoding
 * Format: turn_XXXXXX (6 random alphanumeric chars)
 */
function generateQRCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let result = 'turn_';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Build the full URL that will be encoded in the QR code
 * Uses the production domain for consistency
 */
export function buildQRUrl(code: string): string {
  // Always use production URL for QR codes
  // This ensures QR codes work regardless of where they're generated
  const baseUrl = 'https://parent.turnliteracy.com';
  return `${baseUrl}/qr/${code}`;
}

// =============================================================================
// SERVICE FUNCTIONS
// =============================================================================

/**
 * List all QR codes (admin only)
 */
export async function listQRCodes(): Promise<{ data: QRCode[] | null; error: Error | null }> {
  const { data, error } = await qrCodesTable()
    .select('*')
    .order('created_at', { ascending: false });
  
  return { data: data as QRCode[] | null, error: error as Error | null };
}

/**
 * Get a single QR code by ID
 */
export async function getQRCode(id: string): Promise<{ data: QRCode | null; error: Error | null }> {
  const { data, error } = await qrCodesTable()
    .select('*')
    .eq('id', id)
    .maybeSingle();
  
  return { data: data as QRCode | null, error: error as Error | null };
}

/**
 * Extract QR code from scanned value
 * Handles both raw codes (turn_XXXXX) and full URLs (https://parent.turnliteracy.com/qr/turn_XXXXX)
 */
function extractQRCode(scannedValue: string): string {
  // If it's a full URL, extract just the code
  const urlMatch = scannedValue.match(/\/qr\/(turn_[A-Za-z0-9]+)$/);
  if (urlMatch) {
    return urlMatch[1];
  }
  
  // Also handle any URL with turn_ code at the end
  const turnCodeMatch = scannedValue.match(/(turn_[A-Za-z0-9]+)$/);
  if (turnCodeMatch) {
    return turnCodeMatch[1];
  }
  
  // Return as-is (might be a raw code)
  return scannedValue;
}

/**
 * Look up a QR code by its code (for scanning)
 * Handles both raw codes and full URLs from scanner
 */
export async function lookupQRCode(scannedValue: string): Promise<{ data: QRCode | null; error: Error | null }> {
  // Extract just the code portion if a URL was scanned
  const code = extractQRCode(scannedValue);
  
  console.log('[QR] Looking up code:', { scannedValue, extractedCode: code });
  
  // Use maybeSingle to handle zero rows gracefully (returns null instead of error)
  // NOTE: Type assertion needed because qr_codes table may not be in generated types
  const result = await qrCodesTable()
    .select('*')
    .eq('code', code)
    .eq('is_active', true)
    .maybeSingle();
  
  // Cast result to expected shape
  const data = result.data as QRCode | null;
  const error = result.error as Error | null;
  
  if (error) {
    console.error('[QR] Lookup error:', error);
    return { data: null, error };
  }
  
  if (!data) {
    console.log('[QR] No matching QR code found for:', code);
    return { data: null, error: new Error(`QR code not found: ${code}`) };
  }
  
  console.log('[QR] Found QR code:', { id: data.id, name: data.name, action_type: data.action_type });
  return { data, error: null };
}

/**
 * Create a new QR code
 */
export async function createQRCode(
  input: CreateQRCodeInput,
  createdBy: string
): Promise<{ data: QRCode | null; error: Error | null }> {
  const code = input.code || generateQRCode();
  
  const { data, error } = await qrCodesTable()
    .insert({
      name: input.name,
      code,
      action_type: input.action_type || null,
      action_payload: input.action_payload || null,
      created_by: createdBy,
    })
    .select()
    .single();
  
  return { data: data as QRCode | null, error: error as Error | null };
}

/**
 * Update an existing QR code
 */
export async function updateQRCode(
  id: string,
  input: UpdateQRCodeInput
): Promise<{ data: QRCode | null; error: Error | null }> {
  const { data, error } = await qrCodesTable()
    .update(input)
    .eq('id', id)
    .select()
    .single();
  
  return { data: data as QRCode | null, error: error as Error | null };
}

/**
 * Delete a QR code
 */
export async function deleteQRCode(id: string): Promise<{ error: Error | null }> {
  const { error } = await qrCodesTable()
    .delete()
    .eq('id', id);
  
  return { error: error as Error | null };
}

/**
 * Toggle QR code active status
 */
export async function toggleQRCodeActive(
  id: string,
  isActive: boolean
): Promise<{ data: QRCode | null; error: Error | null }> {
  return updateQRCode(id, { is_active: isActive });
}

// =============================================================================
// ACTION EXECUTION
// =============================================================================

/**
 * Execute the action associated with a QR code
 * 
 * @param qrCode - The QR code with action_type and action_payload
 * @param navigate - Optional navigation function (for React Router integration)
 * @returns Result of the action execution
 */
export async function executeQRAction(
  qrCode: QRCode,
  navigate?: (route: string) => void
): Promise<QRActionResult> {
  if (!qrCode.action_type || !qrCode.action_payload) {
    return {
      success: false,
      action: null,
      message: 'No action configured for this QR code',
    };
  }

  try {
    switch (qrCode.action_type) {
      case 'play_phoneme': {
        const payload = qrCode.action_payload as QRPlayPhonemePayload;
        if (!payload.phoneme) {
          return {
            success: false,
            action: 'play_phoneme',
            message: 'No phoneme specified in action payload',
          };
        }
        
        // Ensure audio engine is ready (awaits AudioContext resume for browser autoplay policy)
        console.log('[QRCodeService] Ensuring audio is ready...');
        await ensureAudioReady();
        console.log('[QRCodeService] Audio ready, playing phoneme:', payload.phoneme);
        
        await playPhoneme(payload.phoneme);
        
        const displayName = payload.displayName || payload.phoneme;
        console.log('[QRCodeService] Played phoneme:', displayName);
        
        return {
          success: true,
          action: 'play_phoneme',
          message: `Played sound: ${displayName}`,
        };
      }

      case 'navigate': {
        const payload = qrCode.action_payload as QRNavigatePayload;
        if (!payload.route) {
          return {
            success: false,
            action: 'navigate',
            message: 'No route specified in action payload',
          };
        }
        
        if (navigate) {
          // Use React Router navigation
          navigate(payload.route);
        } else {
          // Fallback to window.location
          window.location.href = payload.route;
        }
        
        console.log('[QRCodeService] Navigated to:', payload.route);
        
        return {
          success: true,
          action: 'navigate',
          message: `Navigated to: ${payload.route}`,
        };
      }

      default:
        return {
          success: false,
          action: qrCode.action_type,
          message: `Unknown action type: ${qrCode.action_type}`,
        };
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Action execution failed';
    console.error('[QRCodeService] Action execution error:', error);
    
    return {
      success: false,
      action: qrCode.action_type,
      message: errorMessage,
    };
  }
}

/**
 * Scan and execute - convenience function that looks up QR code and executes action
 * 
 * @param code - The QR code string to look up
 * @param navigate - Optional navigation function
 * @returns Result of the scan and action execution
 */
export async function scanAndExecute(
  code: string,
  navigate?: (route: string) => void
): Promise<QRActionResult & { qrCode?: QRCode }> {
  // Look up QR code
  const { data: qrCode, error } = await lookupQRCode(code);
  
  if (error || !qrCode) {
    return {
      success: false,
      action: null,
      message: error?.message || 'QR code not found or inactive',
    };
  }
  
  // Execute action
  const result = await executeQRAction(qrCode, navigate);
  
  return {
    ...result,
    qrCode,
  };
}

/**
 * Scan and execute with card - for consonant practice
 * Returns QR code with card_image_path for display
 * 
 * @param code - The QR code string to look up
 * @returns Result with QR code including card image path
 */
export async function scanAndExecuteWithCard(
  code: string
): Promise<QRActionResult & { qrCode?: QRCodeWithCard }> {
  // Look up QR code
  const { data: qrCode, error } = await lookupQRCode(code);
  
  if (error || !qrCode) {
    return {
      success: false,
      action: null,
      message: error?.message || 'QR code not found or inactive',
    };
  }
  
  // Execute action (play phoneme)
  const result = await executeQRAction(qrCode);
  
  return {
    ...result,
    qrCode: qrCode as QRCodeWithCard,
  };
}
